<script>
import EzButton from '@/components/ui/Button';
import EzForm from '@/components/ui/Form';
import { EzFormModal } from '@/components/ui/Modal';
import EzInput from '@/components/ui/Input';
import { LOADING_KEY, userModalTab1, userModalTab2, userModalTab3 } from '@/util/constants';
import { mapGetters, mapMutations } from 'vuex';
import uuid from 'uuid/v4';
import { EzTab, EzTabs } from '@/components/ui/Tabs';
import EzRolesList from '@/views/common/users/EzRolesList';
import { falsy } from '@/util/utils';
import OrderNotificationToggle from '@/views/common/users/OrderNotificationToggle';

export default {
  components: {
    EzButton,
    EzForm,
    EzFormModal,
    EzInput,
    EzTabs,
    EzTab,
    EzRolesList,
    OrderNotificationToggle,
  },
  props: {
    formKey: {
      type: String,
      required: true,
    },
    formAction: {
      type: String,
      required: true,
    },
    user: {
      type: Object,
      required: false,
      default: () => {},
    },
    users: {
      type: Array,
      required: false,
      default: () => [],
    },
    additionalData: {
      type: Object,
      required: false,
      default: null,
    },
    entity: {
      type: String,
      required: false,
      default: 'User',
    },
    roles: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      keyId: null,
      loadingKey: LOADING_KEY.VENUE_SUPPLIER_ADD_USER,
      userModalTab1,
      userModalTab2,
      userModalTab3,
      tabsFields: {
        [userModalTab1]: ['name', 'email', 'phone'],
        [userModalTab2]: ['roleKey'],
        [userModalTab3]: [],
      },
      settings: {
        email: false,
        whatsApp: false,
        sms: false,
      },
    };
  },
  computed: {
    ...mapGetters('errors', [
      'getFormErrors',
    ]),
    getErrors() {
      return this.getFormErrors(this.formKey) || {};
    },
    hasErrors() {
      return !falsy(this.getErrors);
    },
    defaultData() {
      return {
        optionalPermissions: [],
        ...this.additionalData,
      };
    },
    isExistingUser() {
      return this.user && !!this.user.id;
    },
    method() {
      return this.isExistingUser ? 'patch' : 'post';
    },
    action() {
      if (!this.isExistingUser) {
        return this.formAction;
      }

      return `${this.formAction}/${this.user.id}`;
    },
    ...mapGetters('loading', ['getLoading']),
    isLoadingUser() {
      return this.getLoading(LOADING_KEY.VENUE_SUPPLIER_ADD_USER);
    },
  },
  watch: {
    user(val) {
      this.user = val;
      this.keyId = uuid();
      this.settings = {
        ...this.user?.notificationSettings,
      };
    },
    hasErrors(val) {
      if (val) this.changeTab();
    },
  },
  methods: {
    ...mapMutations('errors', [
      'CLEAR_ERRORS',
    ]),
    open() {
      this.$refs.tabs.selectTab(userModalTab1);
      this.$refs.userModal.open();
    },
    close() {
      this.$refs.userModal.close();
    },
    onClose() {
      this.CLEAR_ERRORS({ key: this.formKey });
      this.$refs.notifySettings.reset();
    },
    submitForm() {
      this.$refs.form.onSubmit();
    },
    onSubmitSuccess(result) {
      this.$emit('success', { ...result, isUpdate: this.isExistingUser });
      if (!this.isExistingUser) this.$refs.form.reset();
    },
    onSubmitError(result) {
      this.$emit('error', result);
    },
    checkError(tab) {
      const errors = Object.keys(this.getErrors);
      return errors.some(err => this.tabsFields[tab].includes(err));
    },
    selectTab(tab) {
      this.$refs.tabs.selectTab(tab);
    },
    changeTab() {
      if (this.hasErrors) {
        switch (true) {
          case this.checkError(userModalTab1):
            this.selectTab(userModalTab1);
            break;
          case this.checkError(userModalTab2):
            this.selectTab(userModalTab2);
            break;
          case this.checkError(userModalTab3):
            this.selectTab(userModalTab3);
            break;
          default: this.selectTab(userModalTab1);
        }
      }
    },
    setNotifySettings(settings) {
      this.settings = {
        ...settings,
      };
    },
    transformer(data) {
      const tst = {
        ...data,
        notificationSettings: { ...this.settings },
      };
      return tst;
    },
  },
};
</script>

<template>
  <ez-form-modal
    @close="onClose"
    ref="userModal"
    class="ez-user-modal">
    <template #title>
      <slot name="title">{{ isExistingUser ? `${entity} Details` : `Add New ${entity}` }}</slot>
    </template>
    <template #content>
      <ez-form
        :key="keyId"
        ref="form"
        :formKey="formKey"
        :additional-headers="{ loadingKey }"
        :action="action"
        :method="method"
        :default-data="defaultData"
        :transformer="transformer"
        @success="onSubmitSuccess"
        @error="onSubmitError">
        <ez-tabs ref="tabs">
          <ez-tab :title="`${entity} Info`" :hash="userModalTab1">
            <ez-input
              class="mt-16"
              :formKey="formKey"
              label="Name"
              name="name"
              placeholder="Enter Full Name"
              :value="user.name" />
            <ez-input
              :formKey="formKey"
              label="Email Address"
              placeholder="Enter Email Address"
              name="email"
              :value="user.email" />
            <ez-input
              class="mt-12"
              :formKey="formKey"
              :value="user.phone || ''"
              placeholder="Enter Phone Number"
              name="phone"
              label="Phone Number" />
          </ez-tab>
          <ez-tab title="Role & Permissions" :hash="userModalTab2">
            <EzRolesList
              ref="roles"
              :formKey="formKey"
              :user="user"
              :roles="roles"
              :users="users"
            />
          </ez-tab>
          <ez-tab title="Order Notifications" :hash="userModalTab3">
            <order-notification-toggle
              :user="user"
              ref="notifySettings"
              :form-key="formKey"
              @change="setNotifySettings"
            />
          </ez-tab>
        </ez-tabs>
      </ez-form>
    </template>
    <template #footer>
      <ez-button
        type="link"
        formType="button"
        @click="close">Cancel</ez-button>
      <ez-button
        formType="button"
        @click="submitForm"
        :is-loading="isLoadingUser"
      >
        {{ isExistingUser ? 'Update Details' : `Add ${entity}` }}
      </ez-button>
    </template>
  </ez-form-modal>
</template>

<style scoped lang="scss">
.ez-user-modal {
  :deep() .ez-dropdown {
    max-width: 100%;
  }
  :deep() .modal {
    overflow: visible;
  }
  .ez-form {
    .input-group + .input-group {
      margin-top: .75rem;
    }
  }
}
:deep() .ez-tabs {
  &__buttons {
    & > li:first-child {
      button {
        margin-left: 0;
      }
    }
  }
  &__content {
    overflow: inherit;
  }
}
:deep() .role-dropdown {
  border-top: none;
  margin-top: 0;
  padding-top: 16px;
}
</style>
